import React from "react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ChatWidget from "@/components/site/ChatWidget";
import CallbackForm from "@/components/site/CallbackForm";
import LeadForm from "@/components/site/LeadForm";
import { Button } from "@/components/ui/button";
import {
  Stethoscope, HeartPulse, Syringe, Activity, Bandage, Baby, Phone, MessageCircle,
  MapPin, Clock, ShieldCheck, ArrowRight, Moon, ChevronRight, Mail, Plane, Building2, Sparkles
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1760633549227-901e0c3cf9d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGZhbWlseSUyMG91dGRvb3JzJTIwZ29sZGVuJTIwaG91cnxlbnwwfHx8fDE3NzY3NjM1MjZ8MA&ixlib=rb-4.1.0&q=85";
const HERO_FALLBACK = "https://images.pexels.com/photos/3042436/pexels-photo-3042436.jpeg?auto=compress&cs=tinysrgb&w=1920";
const DR_LOUW = "https://customer-assets.emergentagent.com/job_oakmed-care/artifacts/pdctmrta_IMG_6424.jpeg";
const DR_VAN_ECK = "https://customer-assets.emergentagent.com/job_oakmed-care/artifacts/anj7mdzu_415F9F73-F12B-4A06-ABCF-81955E74A68F.jpeg";
const CLINIC = "https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=1280";

const SERVICES = [
  { icon: Stethoscope, title: "Family Medicine", body: "Comprehensive care for every age — newborns to grandparents." },
  { icon: HeartPulse, title: "Vitality Health Checks", body: "Blood pressure, glucose, cholesterol, BMI & preventive screens." },
  { icon: Syringe, title: "Child Vaccinations", body: "Full immunisation schedules via Unicare Pharmacy." },
  { icon: Activity, title: "Chronic Disease Management", body: "Ongoing support for diabetes, hypertension & more." },
  { icon: Bandage, title: "Wound Care", body: "Professional treatment for cuts, burns & post-op wounds." },
  { icon: Baby, title: "Family Planning", body: "Confidential consultations and contraceptive options." },
];

export default function LandingPage() {
  return (
    <div id="top" className="min-h-screen bg-cream">
      <Header />

      {/* HERO */}
      <section className="relative pt-16" data-testid="hero-section">
        <div className="relative h-[82vh] min-h-[560px] w-full overflow-hidden">
          <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { if (e.currentTarget.src !== HERO_FALLBACK) e.currentTarget.src = HERO_FALLBACK; }} />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 grain-bg" />

          <div className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-end pb-16 sm:pb-24">
            <div className="max-w-2xl animate-fade-up">
              <p className="eyebrow text-sage/90">Family Practice · Bellville</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl mt-3 font-display font-semibold leading-[1.05] text-cream">
                Where your family's health comes first.
              </h1>
              <p className="mt-5 text-cream/90 text-base sm:text-lg max-w-xl leading-relaxed">
                Compassionate, personalised medical care from Dr Du Pré Louw and Dr Tanya Van Eck —
                now with a 24/7 virtual assistant and never-miss-a-call callback system.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button asChild className="bg-clay hover:bg-clay-dark text-cream h-12 px-6 rounded-lg text-base" data-testid="hero-book-now">
                  <a href="https://mygc.co.za/external/diary/b34126f8-72c0-41cf-b8da-d716b2ea8733" target="_blank" rel="noreferrer">
                    Book an appointment <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                </Button>
                <Button asChild variant="outline" className="h-12 px-6 rounded-lg text-base border-cream/50 text-cream hover:bg-cream hover:text-forest bg-transparent" data-testid="hero-callback">
                  <a href="#callback"><Phone className="w-4 h-4 mr-2" /> Request a callback</a>
                </Button>
              </div>
              <div className="mt-10 flex flex-wrap gap-5 text-xs tracking-widest uppercase text-cream/75">
                <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-sage"/> Medical aids accepted</span>
                <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-sage"/> Mon – Fri · 8am – 5pm</span>
                <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-sage"/> Oakmed, Bellville</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats ribbon */}
        <div className="max-w-7xl mx-auto px-5 sm:px-8 -mt-12 relative z-20" data-testid="stats-ribbon">
          <div className="bg-cream border border-border rounded-2xl p-6 sm:p-8 grid grid-cols-3 gap-4 shadow-sm">
            {[
              { n: "15+", l: "Years experience" },
              { n: "5 000+", l: "Patients served" },
              { n: "100%", l: "Dedication" },
            ].map((s) => (
              <div key={s.l} className="text-center sm:text-left">
                <div className="font-display text-3xl sm:text-4xl text-forest">{s.n}</div>
                <div className="text-[11px] tracking-widest uppercase text-ink/60 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AFTER-HOURS BANNER */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-14" data-testid="afterhours-banner">
        <div className="bg-forest text-cream rounded-2xl px-6 sm:px-10 py-6 sm:py-8 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-clay/20 text-clay flex items-center justify-center shrink-0">
              <Moon className="w-5 h-5"/>
            </div>
            <div>
              <div className="eyebrow text-sage">After-hours care</div>
              <div className="font-display text-xl sm:text-2xl mt-1">Uniclinic Latecare is here when the practice is closed.</div>
              <p className="text-cream/70 text-sm mt-1">Weekdays 6pm – 5am · Sat & Sun all night. 224 Durban Road, Bellville.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button asChild className="bg-clay hover:bg-clay-dark text-cream" data-testid="afterhours-call">
              <a href="tel:0675923217"><Phone className="w-4 h-4 mr-2"/> 067 592 3217</a>
            </Button>
            <Button asChild variant="outline" className="bg-transparent border-cream/30 text-cream hover:bg-cream hover:text-forest" data-testid="afterhours-whatsapp">
              <a href="https://wa.me/27675923218" target="_blank" rel="noreferrer">
                <MessageCircle className="w-4 h-4 mr-2"/> WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="max-w-7xl mx-auto px-5 sm:px-8 py-24" data-testid="services-section">
        <div className="max-w-3xl">
          <p className="eyebrow">What we do</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-medium text-forest mt-3">
            Comprehensive care, every stage of life.
          </h2>
          <p className="text-ink/75 mt-4 leading-relaxed text-base">
            From routine check-ups to chronic care, we blend modern medicine with a warm, personal touch.
            Every visit is unhurried, honest and built around you.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <div
              key={s.title}
              className="group bg-white border border-border rounded-xl p-7 hover:-translate-y-1 hover:shadow-md transition-all"
              data-testid={`service-card-${i}`}
            >
              <div className="w-11 h-11 rounded-lg bg-sage/60 text-forest flex items-center justify-center">
                <s.icon className="w-5 h-5" />
              </div>
              <h3 className="font-display text-2xl text-forest mt-5">{s.title}</h3>
              <p className="text-ink/70 text-sm mt-2 leading-relaxed">{s.body}</p>
              <a
                href="https://mygc.co.za/external/diary/b34126f8-72c0-41cf-b8da-d716b2ea8733"
                target="_blank"
                rel="noreferrer"
                className="mt-5 inline-flex items-center gap-1.5 text-clay text-sm font-medium group-hover:gap-2.5 transition-all"
                data-testid={`service-book-${i}`}
              >
                Book this <ChevronRight className="w-4 h-4"/>
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* DOCTORS */}
      <section id="doctors" className="bg-cream-alt/60 py-24" data-testid="doctors-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <p className="eyebrow">Meet your doctors</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-medium text-forest mt-3">Kind faces. Sharp minds.</h2>
            </div>
            <p className="text-ink/70 max-w-md text-sm">
              Each patient is seen by a doctor who truly listens. Choose the doctor that suits you and book directly below.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              { img: DR_LOUW, name: "Dr Du Pré Louw", role: "Family Doctor · Oakmed Medical Centre", whatsapp: "https://wa.me/27723994711", book: "https://mygc.co.za/external/diary/b34126f8-72c0-41cf-b8da-d716b2ea8733", email: "duprelouw@gmail.com" },
              { img: DR_VAN_ECK, name: "Dr Tanya Van Eck", role: "Family Doctor · Unicare Day Clinic", whatsapp: "https://wa.me/27689569622", book: "https://mygc.co.za/external/practice/dce1e6d0-b206-43e3-ba52-9ac6378dbdd7/first-timeslot", email: "doctor.van.eck@gmail.com" },
            ].map((d, i) => (
              <div key={d.name} className="bg-white border border-border rounded-2xl overflow-hidden flex flex-col sm:flex-row" data-testid={`doctor-card-${i}`}>
                <div className="sm:w-2/5 h-64 sm:h-auto">
                  <img src={d.img} alt={d.name} className="w-full h-full object-cover"/>
                </div>
                <div className="p-6 sm:p-7 flex flex-col">
                  <p className="eyebrow">Doctor</p>
                  <h3 className="font-display text-2xl sm:text-3xl text-forest mt-2">{d.name}</h3>
                  <p className="text-sm text-ink/70 mt-1">{d.role}</p>
                  <div className="mt-auto pt-6 flex flex-wrap gap-2">
                    <Button asChild className="bg-clay hover:bg-clay-dark text-cream" data-testid={`doctor-book-${i}`}>
                      <a href={d.book} target="_blank" rel="noreferrer">Book appointment</a>
                    </Button>
                    <Button asChild variant="outline" className="border-forest/30 hover:bg-forest hover:text-cream" data-testid={`doctor-whatsapp-${i}`}>
                      <a href={d.whatsapp} target="_blank" rel="noreferrer"><MessageCircle className="w-4 h-4 mr-1.5"/> WhatsApp</a>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HEALTHCARE PARTNERS */}
      <section id="partners" className="max-w-7xl mx-auto px-5 sm:px-8 py-24" data-testid="partners-section">
        <div className="max-w-3xl mb-12">
          <p className="eyebrow">Our healthcare partners</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-medium text-forest mt-3">
            A network of trusted care.
          </h2>
          <p className="text-ink/75 mt-4 leading-relaxed text-base">
            We work hand-in-hand with trusted partners in Bellville to bring you comprehensive healthcare under one roof —
            from routine medicines and vaccinations to after-hours emergencies.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Dr Tanya Van Eck Unicare Day Clinic */}
          <div className="bg-white border border-border rounded-2xl p-7 flex flex-col" data-testid="partner-unicare-dayclinic">
            <div className="w-11 h-11 rounded-lg bg-sage/60 text-forest flex items-center justify-center">
              <Stethoscope className="w-5 h-5" />
            </div>
            <p className="eyebrow mt-5">Satellite practice</p>
            <h3 className="font-display text-2xl text-forest mt-2">Dr Tanya Van Eck · Unicare Day Clinic</h3>
            <p className="text-ink/70 text-sm mt-2 leading-relaxed">
              A satellite practice of Dr Du Pré Louw, on the upper level of Unicare Pharmacy Bellville.
            </p>
            <ul className="text-sm text-ink/80 mt-5 space-y-2">
              <li className="flex gap-2"><MapPin className="w-4 h-4 shrink-0 text-clay mt-0.5"/> 224 Durban Road, Bellville (Upper Level)</li>
              <li className="flex gap-2"><Clock className="w-4 h-4 shrink-0 text-clay mt-0.5"/> Mon – Fri · 8am – 5pm</li>
              <li className="flex gap-2"><Phone className="w-4 h-4 shrink-0 text-clay mt-0.5"/> <a href="tel:0795115737" className="hover:text-clay" data-testid="partner-dayclinic-phone">079 511 5737</a></li>
              <li className="flex gap-2"><Mail className="w-4 h-4 shrink-0 text-clay mt-0.5"/> <a href="mailto:dlouw.unicare@gmail.com" className="hover:text-clay break-all" data-testid="partner-dayclinic-email">dlouw.unicare@gmail.com</a></li>
            </ul>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Unicare+Pharmacy+224+Durban+Road+Bellville"
              target="_blank"
              rel="noreferrer"
              className="mt-auto pt-6 inline-flex items-center gap-1.5 text-clay text-sm font-medium hover:gap-2.5 transition-all"
              data-testid="partner-dayclinic-map"
            >
              View on Google Maps <ChevronRight className="w-4 h-4"/>
            </a>
          </div>

          {/* Unicare Pharmacy Bellville */}
          <div className="bg-white border border-border rounded-2xl p-7 flex flex-col" data-testid="partner-unicare-pharmacy">
            <div className="w-11 h-11 rounded-lg bg-sage/60 text-forest flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
            <p className="eyebrow mt-5">Pharmacy partner</p>
            <h3 className="font-display text-2xl text-forest mt-2">Unicare Pharmacy Bellville</h3>
            <p className="text-ink/70 text-sm mt-2 leading-relaxed">
              Medications, child vaccinations, wound care, family planning supplies and a full travel clinic — all in one place.
            </p>
            <div className="flex flex-wrap gap-1.5 mt-4">
              {[
                { label: "Vaccinations", Icon: Syringe },
                { label: "Wound Care", Icon: Bandage },
                { label: "Travel Clinic", Icon: Plane },
                { label: "Family Planning", Icon: Baby },
              ].map((t) => (
                <span key={t.label} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-cream-alt text-ink text-[11px] rounded-full border border-border">
                  <t.Icon className="w-3 h-3 text-clay"/> {t.label}
                </span>
              ))}
            </div>
            <ul className="text-sm text-ink/80 mt-5 space-y-2">
              <li className="flex gap-2"><MapPin className="w-4 h-4 shrink-0 text-clay mt-0.5"/> 224 Durban Road, Bellville, 7530</li>
              <li className="flex gap-2"><Phone className="w-4 h-4 shrink-0 text-clay mt-0.5"/> <a href="tel:0219484566" className="hover:text-clay" data-testid="partner-pharmacy-phone">021 948 4566</a></li>
            </ul>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Unicare+Pharmacy+224+Durban+Road+Bellville"
              target="_blank"
              rel="noreferrer"
              className="mt-auto pt-6 inline-flex items-center gap-1.5 text-clay text-sm font-medium hover:gap-2.5 transition-all"
              data-testid="partner-pharmacy-map"
            >
              View on Google Maps <ChevronRight className="w-4 h-4"/>
            </a>
          </div>

          {/* Uniclinic Latecare */}
          <div className="bg-forest text-cream rounded-2xl p-7 flex flex-col" data-testid="partner-uniclinic-latecare">
            <div className="w-11 h-11 rounded-lg bg-clay/20 text-clay flex items-center justify-center">
              <Moon className="w-5 h-5" />
            </div>
            <p className="eyebrow mt-5 text-sage">After-hours care</p>
            <h3 className="font-display text-2xl mt-2">Uniclinic Latecare</h3>
            <p className="text-cream/75 text-sm mt-2 leading-relaxed">
              Dr Du Pré Louw is a director of Uniclinic Latecare — providing after-hours medical care when you need it most,
              on the upper level of Unicare Pharmacy Bellville.
            </p>
            <ul className="text-sm text-cream/85 mt-5 space-y-2">
              <li className="flex gap-2"><MapPin className="w-4 h-4 shrink-0 text-clay mt-0.5"/> 224 Durban Road, Bellville (Upper Level)</li>
              <li className="flex gap-2"><Clock className="w-4 h-4 shrink-0 text-clay mt-0.5"/>
                <span>
                  Weekdays 6pm – 5am<br/>
                  Sat 8am – 5am · Sun 8am – 5am
                </span>
              </li>
              <li className="flex gap-2"><Phone className="w-4 h-4 shrink-0 text-clay mt-0.5"/> <a href="tel:0675923217" className="hover:text-clay" data-testid="partner-latecare-phone">067 592 3217</a> <span className="text-cream/60">· Staff on Duty</span></li>
              <li className="flex gap-2"><MessageCircle className="w-4 h-4 shrink-0 text-clay mt-0.5"/> <a href="https://wa.me/27675923218" target="_blank" rel="noreferrer" className="hover:text-clay" data-testid="partner-latecare-whatsapp">WhatsApp 067 592 3218</a> <span className="text-cream/60">· Office hours</span></li>
              <li className="flex gap-2"><Sparkles className="w-4 h-4 shrink-0 text-clay mt-0.5"/> <a href="https://uniclinic.co.za/" target="_blank" rel="noreferrer" className="hover:text-clay" data-testid="partner-latecare-site">uniclinic.co.za</a></li>
            </ul>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Unicare+Pharmacy+224+Durban+Road+Bellville"
              target="_blank"
              rel="noreferrer"
              className="mt-auto pt-6 inline-flex items-center gap-1.5 text-clay text-sm font-medium hover:gap-2.5 transition-all"
              data-testid="partner-latecare-map"
            >
              View on Google Maps <ChevronRight className="w-4 h-4"/>
            </a>
          </div>
        </div>
      </section>

      {/* CALLBACK + LEAD FORM */}
      <section id="callback" className="max-w-7xl mx-auto px-5 sm:px-8 py-24" data-testid="callback-section">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="relative">
            <img src={CLINIC} alt="" className="rounded-2xl w-full h-[420px] object-cover"/>
            <div className="absolute -bottom-6 left-6 right-6 sm:left-auto sm:right-6 sm:w-80 bg-cream border border-border rounded-xl p-5 shadow-md">
              <p className="eyebrow">Never miss a call</p>
              <p className="text-ink/80 text-sm mt-2 leading-relaxed">
                Line busy? Left a voicemail? Fill in the form — we'll call <span className="font-semibold">you</span> back.
              </p>
            </div>
          </div>

          <div className="bg-white border border-border rounded-2xl p-6 sm:p-10">
            <p className="eyebrow">Request a callback</p>
            <h2 className="text-3xl sm:text-4xl font-display text-forest mt-2">We'll call you when it suits.</h2>
            <p className="text-ink/70 text-sm mt-2">Drop your details below. Most callbacks happen within 30 minutes during practice hours.</p>
            <div className="mt-6"><CallbackForm /></div>
          </div>
        </div>

        {/* Lead (appointment) form */}
        <div id="appointment" className="mt-20 grid lg:grid-cols-[2fr_3fr] gap-10 items-start" data-testid="appointment-section">
          <div>
            <p className="eyebrow">Prefer email first?</p>
            <h2 className="text-3xl sm:text-4xl font-display text-forest mt-2">Tell us what you need.</h2>
            <p className="text-ink/70 text-sm mt-3 leading-relaxed">
              Whether it's a repeat script, a new health concern or booking your family's annual check-up —
              share a few details and we'll confirm your appointment, usually same-day.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-ink/80">
              <li className="flex gap-2"><ShieldCheck className="w-5 h-5 text-clay shrink-0"/> Private, secure and confidential</li>
              <li className="flex gap-2"><Clock className="w-5 h-5 text-clay shrink-0"/> Same-day response during office hours</li>
              <li className="flex gap-2"><HeartPulse className="w-5 h-5 text-clay shrink-0"/> Handled by the practice, not a call centre</li>
            </ul>
          </div>
          <div className="bg-white border border-border rounded-2xl p-6 sm:p-10">
            <LeadForm />
          </div>
        </div>
      </section>

      <Footer />
      <ChatWidget />
    </div>
  );
}
